import { useMemo } from "react"
import { useQuery } from "urql"

import "../../../graphql/queries"

import {
  GetProductInventoryPalletGroupsDocument,
  type InventoryPalletGroup,
  InventoryPalletGroupsSortField,
  SortingOrder,
} from "@/graphql/codegen/graphql"

interface UseGetProductInventoryPalletGroupsParams {
  prodNbr: number
  whsNbr?: number
  search?: string
  sortBy?: InventoryPalletGroupsSortField
  sortDirection?: SortingOrder
}

interface UseGetProductInventoryPalletGroupsResponse {
  palletGroups: InventoryPalletGroup[]
  isFetching: boolean
  error: string | undefined
}

export const useGetProductInventoryPalletGroups = ({
  prodNbr,
  whsNbr,
  search,
  sortBy = InventoryPalletGroupsSortField.ExpirationDate,
  sortDirection = SortingOrder.Asc,
}: UseGetProductInventoryPalletGroupsParams): UseGetProductInventoryPalletGroupsResponse => {
  const [{ data, fetching, error }] = useQuery({
    query: GetProductInventoryPalletGroupsDocument,
    variables: {
      input: {
        prodNbr,
        whsNbr: whsNbr ?? null,
        search: search || null,
        sortBy,
        sortDirection,
        offset: 0,
        limit: 500,
      },
    },
  })

  const palletGroups = useMemo(() => {
    const result = data?.inventory?.listPalletGroups
    if (result?.__typename === "InventoryPalletGroupsSuccess") {
      return result.items as InventoryPalletGroup[]
    }
    return []
  }, [data])

  const apiError = useMemo(() => {
    if (error) return error.message
    const result = data?.inventory?.listPalletGroups
    if (result?.__typename === "InventoryPalletGroupsFailure") {
      return result.error.message
    }
    return undefined
  }, [data, error])

  return { palletGroups, isFetching: fetching, error: apiError }
}
